import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VisibilityIcon from '@mui/icons-material/Visibility';
import config from '../../../config';

const STATUS_COLOR = {
  Active: { bg: '#fef3c7', color: '#92400e' },
  Closed: { bg: '#dcfce7', color: '#166534' },
};

// HR Employee Loans — principal + EMI schedule + EMI ledger view. EMIs
// can be recorded manually here; later, payroll will push them in.
export default function Loans() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empFilter, setEmpFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  const today = new Date().toISOString().slice(0, 10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    loanAmount: '',
    emiAmount: '',
    startMonth: today,
    totalMonths: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);

  // EMI ledger view
  const [emiOpen, setEmiOpen] = useState(false);
  const [emiTarget, setEmiTarget] = useState(null);
  const [emiList, setEmiList] = useState([]);
  const [emiForm, setEmiForm] = useState({ paidAt: today, amount: '' });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Employees/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (empFilter) qs.append('employeeId', empFilter);
      if (statusFilter) qs.append('status', statusFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Loans/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load loans', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, empFilter, statusFilter]);

  const totals = useMemo(() => {
    return {
      count: rows.length,
      issued: rows.reduce((a, r) => a + Number(r.loanAmount || 0), 0),
      paid: rows.reduce((a, r) => a + Number(r.paidAmount || 0), 0),
      outstanding: rows.reduce(
        (a, r) => a + Number(r.remainingBalance || 0),
        0,
      ),
    };
  }, [rows]);

  const save = async () => {
    if (
      !form.employeeId ||
      !form.loanAmount ||
      !form.emiAmount ||
      !form.totalMonths
    ) {
      toast('All loan fields required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: null,
        CompanyId: companyId,
        EmployeeId: Number(form.employeeId),
        LoanAmount: Number(form.loanAmount),
        EmiAmount: Number(form.emiAmount),
        StartMonth: form.startMonth,
        TotalMonths: Number(form.totalMonths),
        Reason: form.reason || null,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Loan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast('Loan issued');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEmi = async (r) => {
    setEmiTarget(r);
    setEmiForm({ paidAt: today, amount: r.emiAmount || '' });
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/LoanEmis/${r.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) setEmiList(await res.json());
    } catch (e) {
      setEmiList([]);
    }
    setEmiOpen(true);
  };

  const saveEmi = async () => {
    if (!emiTarget || !emiForm.amount) {
      toast('Enter amount', 'warning');
      return;
    }
    const body = {
      CompanyId: companyId,
      LoanId: emiTarget.id,
      PaidAt: emiForm.paidAt,
      Amount: Number(emiForm.amount),
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/LoanEmi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast('Save failed', 'error');
      return;
    }
    toast('EMI recorded');
    openEmi(emiTarget);
    load();
  };

  const progressFor = (r) => {
    if (!r.loanAmount) return 0;
    return Math.min(
      100,
      Math.round(
        (Number(r.paidAmount || 0) / Number(r.loanAmount)) * 100,
      ),
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#0d9488',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <AccountBalanceIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Employee Loans
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR · {totals.count} loan(s) · ₹{totals.outstanding.toLocaleString('en-IN')} outstanding
            </Typography>
          </Box>
        </Stack>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => {
            setForm({
              employeeId: '',
              loanAmount: '',
              emiAmount: '',
              startMonth: today,
              totalMonths: '',
              reason: '',
            });
            setDialogOpen(true);
          }}
          sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
          New Loan
        </Button>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Total issued', totals.issued, '#0d9488'],
          ['Paid', totals.paid, '#16a34a'],
          ['Outstanding', totals.outstanding, '#dc2626'],
        ].map(([label, val, color]) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                {label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                ₹{Number(val).toLocaleString('en-IN')}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}>
        <TextField
          select
          size='small'
          label='Employee'
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.employeeName} ({e.employeeCode})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label='Status'
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 160 }}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='Active'>Active</MenuItem>
          <MenuItem value='Closed'>Closed</MenuItem>
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Loan
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      EMI
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tenure</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Paid
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Outstanding
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>
                      Progress
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No loans.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Active;
                    const pct = progressFor(r);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          {new Date(r.startMonth).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.employeeName}
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {r.employeeCode}
                          </Typography>
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          ₹{Number(r.loanAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right'>
                          ₹{Number(r.emiAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>{r.totalMonths} mo</TableCell>
                        <TableCell align='right' sx={{ color: '#16a34a' }}>
                          ₹{Number(r.paidAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#dc2626' }}>
                          ₹{Number(r.remainingBalance).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: '100%', minWidth: 120 }}>
                            <LinearProgress
                              variant='determinate'
                              value={pct}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: '#f1f5f9',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#0d9488',
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: '0.65rem', color: '#64748B', mt: 0.3 }}>
                              {pct}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={r.status}
                            sx={{
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title='EMI ledger / record EMI'>
                            <IconButton size='small' onClick={() => openEmi(r)}>
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Issue loan dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Loan</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Employee *'
                value={form.employeeId}
                onChange={(e) =>
                  setForm({ ...form, employeeId: e.target.value })
                }>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.employeeName} ({e.employeeCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Loan Amount *'
                inputProps={{ min: 0 }}
                value={form.loanAmount}
                onChange={(e) =>
                  setForm({ ...form, loanAmount: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='EMI Amount *'
                inputProps={{ min: 0 }}
                value={form.emiAmount}
                onChange={(e) =>
                  setForm({ ...form, emiAmount: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='Start Month *'
                InputLabelProps={{ shrink: true }}
                value={form.startMonth}
                onChange={(e) =>
                  setForm({ ...form, startMonth: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Total Months *'
                inputProps={{ min: 1 }}
                value={form.totalMonths}
                onChange={(e) =>
                  setForm({ ...form, totalMonths: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Reason'
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
            {saving ? 'Saving…' : 'Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EMI ledger dialog */}
      <Dialog
        open={emiOpen}
        onClose={() => setEmiOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          EMI Ledger
          {emiTarget && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {emiTarget.employeeName} · Loan ₹
              {Number(emiTarget.loanAmount).toLocaleString('en-IN')} · EMI ₹
              {Number(emiTarget.emiAmount).toLocaleString('en-IN')} · Outstanding ₹
              {Number(emiTarget.remainingBalance).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {emiTarget?.status === 'Active' && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 2 }}>
              <TextField
                type='date'
                size='small'
                label='Paid At *'
                InputLabelProps={{ shrink: true }}
                value={emiForm.paidAt}
                onChange={(e) =>
                  setEmiForm({ ...emiForm, paidAt: e.target.value })
                }
              />
              <TextField
                type='number'
                size='small'
                label='Amount *'
                value={emiForm.amount}
                onChange={(e) =>
                  setEmiForm({ ...emiForm, amount: e.target.value })
                }
                inputProps={{ min: 0 }}
              />
              <Button variant='contained' onClick={saveEmi}>
                Record EMI
              </Button>
            </Stack>
          )}
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payroll #</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emiList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align='center' sx={{ py: 3 }}>
                      <Typography sx={{ color: '#94a3b8' }}>
                        No EMIs recorded yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {emiList.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      {new Date(e.paidAt).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      ₹{Number(e.amount).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>{e.payrollId || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmiOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
